import type { SessionEndedReason } from "@/socket/socketTypes";

// Cross-tab session logout coordination.
//
// Two transports cooperate to terminate every tab of the SAME session as
// soon as any of them sees a logout:
//
//   1. BroadcastChannel — fan-out to other tabs in the same browser /
//      same origin (instant, no server round-trip). Used when the user
//      explicitly signs out in one tab.
//
//   2. Socket `sessionEnded` event — fan-out to other tabs/sockets bound
//      to the same SESSION id on the backend (covers backend-initiated
//      terminations, e.g. an admin killing a session, and acts as a
//      fallback when BroadcastChannel is unavailable).
//
// Multi-device intent: a single user with sessions on multiple devices
// stays signed in on the OTHER devices when one device signs out. That
// is why the socket emit targets the session room, not the user room,
// and BroadcastChannel is same-origin only.

export const SESSION_LOGOUT_CHANNEL = "sinek-session-logout";

// Re-exported from socket types so call sites that only depend on the
// BroadcastChannel transport do not have to import socket types directly.
export type SessionLogoutReason = SessionEndedReason;

export interface SessionLogoutMessage {
  type: "session_ended";
  reason: SessionLogoutReason;
  // Tab-unique id used to ignore our own echo. The publishing tab will
  // receive its own message back through BroadcastChannel (in some
  // browsers) and must filter it out to avoid re-handling the logout.
  origin: string;
  // Wall-clock at which the message was produced (ISO string). Useful for
  // log correlation; not used for any business decision.
  emittedAt: string;
}

interface BuildSessionLogoutMessageParams {
  reason: SessionLogoutReason;
  origin: string;
  now: Date;
}

// Deterministic builder — same inputs always produce the same message
// object (Rule 7). `now` is injected so callers control the timestamp.
export const buildSessionLogoutMessage = (
  params: BuildSessionLogoutMessageParams
): SessionLogoutMessage => {
  return {
    type: "session_ended",
    reason: params.reason,
    origin: params.origin,
    emittedAt: params.now.toISOString(),
  };
};

// Type guard — Rule 8 (fail fast / validate early). Reject any message
// that does not match the expected shape rather than blindly trusting
// `event.data`. A schema mismatch is treated as a foreign message and
// ignored upstream with a warning log.
export const isSessionLogoutMessage = (
  raw: unknown
): raw is SessionLogoutMessage => {
  if (!raw || typeof raw !== "object") return false;
  const m = raw as Record<string, unknown>;
  if (m.type !== "session_ended") return false;
  if (typeof m.origin !== "string" || m.origin.length === 0) return false;
  if (typeof m.emittedAt !== "string" || m.emittedAt.length === 0) return false;
  return (
    m.reason === "user_logout" ||
    m.reason === "remote_terminate" ||
    m.reason === "expired"
  );
};

// Generates a tab-unique identifier. Used as the `origin` of each
// outgoing message so the publishing tab can filter its own echo.
// Falls back to a timestamp + random string when `crypto.randomUUID` is
// not available; that fallback is acceptable because a collision would
// at worst cause a single missed echo-suppression (the publishing tab
// would also redirect itself, which is the correct end state anyway).
export const generateTabId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// One-shot publisher: opens a channel, posts the message, closes the
// channel. Used by `useSignOut` to notify sibling tabs immediately after
// a successful sign-out. SSR / unsupported browser is a no-op (Rule 8:
// silent in this single spot is acceptable because the user has already
// signed out locally; the failure mode is "other tabs notice on next API
// call's 401" which the existing interceptor handles).
interface PublishSessionLogoutParams {
  reason: SessionLogoutReason;
  origin: string;
  now: Date;
}

export const publishSessionLogout = (
  params: PublishSessionLogoutParams
): void => {
  if (typeof window === "undefined") return;
  if (typeof BroadcastChannel === "undefined") return;

  const channel = new BroadcastChannel(SESSION_LOGOUT_CHANNEL);
  try {
    channel.postMessage(buildSessionLogoutMessage(params));
  } finally {
    channel.close();
  }
};
